import { ArrivedButton } from '@/components/donation/ArrivedButton'
import { CallDonorButton } from '@/components/donation/CallDonorButton'
import { CancelDonationButton } from '@/components/donation/CancelDonationButton'
import { HeadOutButton } from '@/components/donation/HeadOutButton'
import { MarkCompleteButton } from '@/components/donation/MarkCompleteButton'
import { NavigateButton } from '@/components/donation/NavigateButton'
import { StatusBadge } from '@/components/donation/StatusBadge'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Loader } from '@/components/ui/Loader'
import { Map, MapMarker } from '@/components/ui/Map'
import { formatAddress } from '@/lib/address'
import { formatDisplayDate } from '@/lib/date'
import { getDonationSpecification } from '@/lib/donation'
import { resolveImageUrl } from '@/lib/image-url'
import { formatPhone } from '@/lib/phone'
import { useRouteStore } from '@/store/route'
import { StandardDonationAasmState } from '@types'
import moment from 'moment'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styles from './Stop.module.scss'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_API_KEY || ''

export const StopPage = () => {
  const navigate = useNavigate()
  const charity = useRouteStore((state) => state.charity)
  const { partOfDay, stop } = useParams<{ partOfDay: string; stop: string }>()

  const currentDonation = useRouteStore((state) => state.currentDonation)
  const currentDonationLoading = useRouteStore(
    (state) => state.currentDonationLoading
  )
  const fetchDonation = useRouteStore((state) => state.fetchDonation)
  const selectedDate = useRouteStore((state) => state.selectedDate)

  const isFutureDate = moment(selectedDate).isAfter(moment(), 'day')

  const stopId = Number(stop)
  const isLoading =
    currentDonationLoading ||
    (currentDonation !== null && currentDonation.id !== stopId)

  useEffect(() => {
    if (stop) {
      fetchDonation(stopId)
    }
  }, [stop, stopId, fetchDonation])

  const donation = currentDonation

  if (isLoading || !donation) {
    const title = isLoading ? 'Loading...' : 'Donation not found'
    return (
      <div className={styles.container} data-testid="stop-page-loading">
        <div className={styles.content}>
          <header className={styles.header}>
            <button
              className={styles.backButton}
              onClick={() => navigate(-1)}
              data-testid="stop-back-button"
            >
              <Icon name="chevron-left" size={20} className={styles.backIcon} />
            </button>
            <div className={styles.headerInfo}>
              <p className={styles.donorName}>{title}</p>
            </div>
          </header>
          {isLoading && <Loader testId="stop-loader" />}
        </div>
      </div>
    )
  }

  const address = formatAddress(donation.address)
  const status = donation.aasm_state || StandardDonationAasmState.ENQUEUED

  const handleBack = () => {
    navigate(-1)
  }

  const handleViewAllItems = () => {
    navigate(`/${partOfDay}/${stop}/items`)
  }

  const handleWriteReport = () => {
    navigate(`/${partOfDay}/${stop}/report`)
  }

  const handleCompleteSuccess = () => {
    navigate(-1)
  }

  const handleCancelSuccess = () => {
    navigate(-1)
  }

  const mapMarkers: MapMarker[] = [
    {
      id: donation.id,
      stepNumber: 1,
      status: status ?? StandardDonationAasmState.ACCEPTED,
      lat: donation.address?.lat ?? 0,
      lng: donation.address?.lng ?? 0,
    },
  ]

  const isPending = status === StandardDonationAasmState.ENQUEUED
  const isAccepted = status === StandardDonationAasmState.ACCEPTED
  const isEnRoute = status === StandardDonationAasmState.EN_ROUTED
  const isArrived = status === StandardDonationAasmState.ARRIVED
  const isCanceled = status === StandardDonationAasmState.CANCELED
  const isCompleted = status === StandardDonationAasmState.COMPLETED

  return (
    <div className={styles.container} data-testid="stop-page">
      <div className={styles.content}>
        {/* Header */}
        <header className={styles.header} data-testid="stop-header">
          <button
            className={styles.backButton}
            onClick={handleBack}
            data-testid="stop-back-button"
          >
            <Icon name="chevron-left" size={20} className={styles.backIcon} />
          </button>
          <div className={styles.headerInfo}>
            <p className={styles.donorName} data-testid="stop-donor-name">
              {donation.donor?.name}
            </p>
            <div className={styles.headerBadges}>
              <StatusBadge status={status} testId="stop-status-badge" />
              {donation.report && (
                <Badge
                  text="Reported"
                  variant="warning"
                  icon="flag-report"
                  testId="stop-reported-badge"
                />
              )}
            </div>
          </div>
        </header>

        {/* Donor Details */}
        <section className={styles.section} data-testid="stop-donor-section">
          <h2 className={styles.sectionTitle}>Donor Details</h2>
          <div className={styles.detailRow}>
            <p className={styles.detailLabel}>Donor</p>
            <p
              className={styles.detailValue}
              data-testid="stop-donor-detail-name"
            >
              {donation.donor?.name ?? '-'}
            </p>
          </div>
          <div className={styles.detailRow}>
            <p className={styles.detailLabel}>Email</p>
            <p className={styles.detailValue} data-testid="stop-donor-email">
              {donation.donor?.email ?? '-'}
            </p>
          </div>
          <div className={styles.detailRow}>
            <p className={styles.detailLabel}>Phone</p>
            <div className={styles.phoneRow}>
              <p className={styles.detailValue} data-testid="stop-donor-phone">
                {formatPhone(donation.donor?.phone)}
              </p>
              {!isPending && !isCanceled && !isCompleted && (
                <CallDonorButton
                  phone={donation.donor?.phone}
                  className={styles.callButton}
                />
              )}
            </div>
          </div>
          <div className={styles.detailRow}>
            <p className={styles.detailLabel}>Address</p>
            {isEnRoute ? (
              <div className={styles.phoneRow}>
                <p className={styles.detailValue} data-testid="stop-address">
                  {address || '-'}
                </p>
                <NavigateButton
                  address={address}
                  variant="light"
                  className={styles.callButton}
                />
              </div>
            ) : (
              <p className={styles.detailValue} data-testid="stop-address">
                {address || '-'}
              </p>
            )}
          </div>
          <div className={styles.mapPreview} data-testid="stop-map">
            <Map
              markers={mapMarkers}
              accessToken={MAPBOX_TOKEN}
              showZoomControls={false}
              interactive={true}
              showUserLocation={false}
              showMarkerBadges={false}
            />
          </div>
          {isAccepted && (
            <HeadOutButton
              address={address}
              donationId={donation.id}
              disabled={isFutureDate}
              className={styles.headOutButton}
            />
          )}
          {isEnRoute && (
            <ArrivedButton
              donationId={donation.id}
              disabled={isFutureDate}
              className={styles.headOutButton}
            />
          )}
          {isArrived && (
            <MarkCompleteButton
              donationId={donation.id}
              onSuccess={handleCompleteSuccess}
              disabled={isFutureDate}
              className={styles.headOutButton}
            />
          )}
        </section>

        <div className={styles.divider} />

        {/* Donation Details */}
        <section className={styles.section} data-testid="stop-donation-section">
          <h2 className={styles.sectionTitle}>Donation Details</h2>

          <p className={styles.itemsLabel}>Donation ID</p>
          <p className={styles.donationId} data-testid="stop-donation-id">
            {donation.donation_code ?? `-`}
          </p>

          {getDonationSpecification(donation.items) && (
            <>
              <p className={styles.itemsLabel}>Donation Items</p>
              <p className={styles.donationId} data-testid="stop-donation-spec">
                {getDonationSpecification(donation.items)}
              </p>
            </>
          )}

          {charity?.donation_items && donation.donation_items && (
            <>
              <p className={styles.itemsLabel}>Items Description</p>
              <p className={styles.donationId} data-testid="stop-donation-spec">
                {donation.donation_items}
              </p>
            </>
          )}

          {donation.items &&
            donation.items.filter((item) => item.photo_url).length > 0 && (
              <>
                <p className={styles.itemsLabel}>Donation Items</p>
                <div className={styles.itemsGrid} data-testid="stop-items-grid">
                  {donation.items
                    .filter((item) => item.photo_url)
                    .slice(0, 4)
                    .map((item) => (
                      <div key={item.id} className={styles.itemCard}>
                        {item.photo_url ? (
                          <img
                            src={resolveImageUrl(item.photo_url)}
                            alt={item.name}
                            className={styles.itemImage}
                          />
                        ) : item.icon_url ? (
                          <div className={styles.itemIconWrapper}>
                            <img
                              src={resolveImageUrl(item.icon_url)}
                              alt={item.name}
                              className={styles.itemIcon}
                            />
                          </div>
                        ) : (
                          <div className={styles.itemPlaceholder}>
                            <Icon
                              name="job"
                              size={24}
                              className={styles.placeholderIcon}
                            />
                          </div>
                        )}
                        <span className={styles.itemBadge}>
                          <Icon
                            name="job"
                            size={8}
                            className={styles.itemBadgeIcon}
                          />
                          {item.name}
                        </span>
                      </div>
                    ))}
                </div>

                <Button
                  text="View All Donation Items"
                  variant="default"
                  icon={<Icon name="arrow" size={16} />}
                  onClick={handleViewAllItems}
                  className={styles.viewAllButton}
                  testId="stop-view-all-items"
                />
              </>
            )}
        </section>

        <div className={styles.divider} />

        {/* Instructions */}
        {donation.instructions && (
          <>
            <section
              className={styles.section}
              data-testid="stop-instructions-section"
            >
              <h3 className={styles.instructionsTitle}>Instructions</h3>
              <p
                className={styles.instructionsText}
                data-testid="stop-instructions"
              >
                {donation.instructions}
              </p>
            </section>
            <div className={styles.divider} />
          </>
        )}

        {/* Internal Notes */}
        {donation.donation_notes && (
          <>
            <section
              className={styles.section}
              data-testid="stop-instructions-section"
            >
              <h3 className={styles.instructionsTitle}>Internal Notes</h3>
              <p
                className={styles.instructionsText}
                data-testid="stop-instructions"
              >
                {donation.donation_notes}
              </p>
            </section>
            <div className={styles.divider} />
          </>
        )}

        {/* Report */}
        <section className={styles.section} data-testid="stop-report-section">
          {(isArrived || isCompleted) && (
            <h3 className={styles.sectionTitle}>Report</h3>
          )}

          {donation.report ? (
            <div className={styles.reportCard} data-testid="stop-report-card">
              <Icon
                name="flag-report"
                size={24}
                className={styles.reportIcon}
              />
              <div className={styles.reportDate}>
                <Icon name="calendar" size={14} />
                <span>
                  Reported{' '}
                  {formatDisplayDate(donation.report.reported_at ?? '')}
                </span>
              </div>
              <p className={styles.reportDescription}>
                {donation.report.description}
              </p>
              {donation.report.photos && donation.report.photos.length > 0 && (
                <div className={styles.reportImages}>
                  {donation.report.photos.map((image, index) => (
                    <img
                      key={index}
                      src={resolveImageUrl(image)}
                      alt={`Report image ${index + 1}`}
                      className={styles.reportImage}
                    />
                  ))}
                </div>
              )}
              <Button
                text="Open Report"
                variant="default"
                icon={<Icon name="arrow" size={16} />}
                onClick={handleWriteReport}
                className={styles.openReportButton}
                testId="stop-open-report"
              />
            </div>
          ) : (
            (isArrived || isCompleted) && (
              <Button
                text="Write A Report"
                variant="default"
                icon={<Icon name="arrow" size={16} />}
                onClick={handleWriteReport}
                className={styles.reportButton}
                testId="stop-write-report"
              />
            )
          )}
          {!isPending && !isCanceled && !isCompleted && (
            <CancelDonationButton
              donationId={donation.id}
              onSuccess={handleCancelSuccess}
              className={styles.cancelButton}
            />
          )}
        </section>
      </div>
    </div>
  )
}
